import { Logo } from '@yhelp/ui';
import { LoginButton } from '@/components/molecules';

const FEATURES = [
  {
    title: 'Multi-tenant nativo',
    description:
      'Ambientes isolados por organização, com branding próprio e dados protegidos por design.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
        <path
          d="M12 3L3 8v8l9 5 9-5V8l-9-5z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M12 12l9-5M12 12v9M12 12L3 7" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    title: 'SLA e prioridades',
    description:
      'Prazos, alertas e filas de triagem para que nenhum chamado crítico fique sem resposta.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Operação em tempo real',
    description:
      'Notificações, chat por chamado e infraestrutura global na borda — baixa latência para sua equipe.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
        <path
          d="M4 14a8 8 0 0116 0"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path d="M12 14l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="14" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    title: 'Pronto para escala',
    description:
      'Interface acessível (WCAG AA), dark mode nativo e fluxos pensados para agentes e solicitantes.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
        <path
          d="M4 6h16M4 12h16M4 18h10"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
] as const;

const MOCK_TICKETS = [
  { number: 1042, title: 'Erro ao acessar relatório mensal', tag: 'Troubleshoot', active: true },
  { number: 1038, title: 'Solicitação de novo usuário', tag: 'Implementação', active: false },
  { number: 1031, title: 'Dúvida sobre integração Slack', tag: 'Feedback', active: false },
];

function ProductPreview() {
  return (
    <div
      className="surface-elevated overflow-hidden border-neutral-200 shadow-brand dark:border-neutral-800"
      aria-hidden="true"
    >
      <div className="flex border-b border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950">
        <div className="hidden w-14 shrink-0 bg-yhelp-black sm:block" />
        <div className="flex flex-1 items-center justify-between gap-3 px-4 py-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted">yHelp / Chamados</p>
            <p className="text-sm font-bold text-yhelp-black dark:text-white">Central de suporte</p>
          </div>
          <span className="rounded-md bg-yhelp-yellow px-3 py-1.5 text-xs font-semibold text-yhelp-black">
            Novo chamado
          </span>
        </div>
      </div>
      <div className="flex">
        <aside className="hidden w-14 shrink-0 space-y-2 bg-yhelp-black p-2 sm:block">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`mx-auto h-8 w-8 rounded-lg ${i === 1 ? 'bg-neutral-800 ring-2 ring-yhelp-yellow' : 'bg-neutral-900'}`}
            />
          ))}
        </aside>
        <div className="flex-1 bg-white p-4 dark:bg-neutral-900">
          <div className="mb-3 flex gap-2">
            {['Todos', 'Abertos', 'Em andamento'].map((label, i) => (
              <span
                key={label}
                className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${
                  i === 0
                    ? 'bg-yhelp-yellow text-yhelp-black'
                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
                }`}
              >
                {label}
              </span>
            ))}
          </div>
          <ul className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
            {MOCK_TICKETS.map((t) => (
              <li
                key={t.number}
                className={`flex items-center gap-3 px-3 py-2.5 ${
                  t.active
                    ? 'border-l-2 border-yhelp-yellow bg-yhelp-yellow/5 dark:bg-yhelp-yellow/10'
                    : ''
                }`}
              >
                <span className="font-mono text-[10px] text-muted">#{t.number}</span>
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-yhelp-black dark:text-white">
                  {t.title}
                </span>
                <span className="hidden rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 sm:inline">
                  {t.tag}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-neutral-50 text-yhelp-black dark:bg-neutral-950 dark:text-neutral-100">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-neutral-200/80 bg-white/95 backdrop-blur-md motion-reduce:animate-none dark:border-neutral-800 dark:bg-neutral-950/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
          <a
            href="/"
            className="transition-opacity duration-fast hover:opacity-90 motion-reduce:transition-none"
          >
            <Logo variant="official" size="sm" alt="yHelp — início" />
          </a>
          <nav className="hidden items-center gap-8 md:flex" aria-label="Navegação principal">
            <a
              href="#recursos"
              className="text-sm font-medium text-neutral-600 transition-colors duration-fast hover:text-yhelp-black dark:text-neutral-400 dark:hover:text-white"
            >
              Recursos
            </a>
            <a
              href="#plataforma"
              className="text-sm font-medium text-neutral-600 transition-colors duration-fast hover:text-yhelp-black dark:text-neutral-400 dark:hover:text-white"
            >
              Plataforma
            </a>
          </nav>
          <LoginButton size="sm" loginLabel="Entrar" />
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-neutral-200 dark:border-neutral-800">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-20"
          aria-hidden="true"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-yhelp-yellow/10 blur-3xl dark:bg-yhelp-yellow/5" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 md:grid-cols-2 md:py-24">
          <div className="animate-fade-in-up motion-reduce:animate-none">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wider text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400">
              <span className="h-1.5 w-1.5 rounded-full bg-yhelp-yellow" />
              Helpdesk ySec
            </p>
            <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight md:text-5xl lg:text-[3.25rem]">
              Suporte corporativo com{' '}
              <span className="underline decoration-yhelp-yellow decoration-4 underline-offset-4">
                clareza
              </span>{' '}
              e controle.
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-neutral-600 dark:text-neutral-400">
              yHelp centraliza chamados, SLAs, filas e comunicação da sua equipe — a mesma
              experiência profissional do app, do primeiro login ao fechamento do ticket.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <LoginButton size="lg" loginLabel="Entrar na plataforma" />
              <a
                href="#plataforma"
                className="inline-flex h-12 items-center rounded-md border border-neutral-300 px-5 text-sm font-semibold text-neutral-700 transition-all duration-fast hover:border-yhelp-black hover:text-yhelp-black motion-reduce:transition-none dark:border-neutral-600 dark:text-neutral-300 dark:hover:border-yhelp-yellow dark:hover:text-white"
              >
                Ver a plataforma
              </a>
            </div>
            <dl className="mt-10 grid grid-cols-3 gap-4 border-t border-neutral-200 pt-8 dark:border-neutral-800">
              {[
                { value: '99,9%', label: 'Uptime' },
                { value: 'Multi-tenant', label: 'Isolamento' },
                { value: 'WCAG AA', label: 'Acessível' },
              ].map((item) => (
                <div key={item.label}>
                  <dt className="text-lg font-extrabold text-yhelp-black dark:text-white">
                    {item.value}
                  </dt>
                  <dd className="text-xs text-muted">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="animate-fade-in-up motion-reduce:animate-none opacity-0 [animation-delay:120ms] [animation-fill-mode:forwards]">
            <ProductPreview />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="recursos" className="mx-auto max-w-6xl px-6 py-20 md:py-28">
        <div className="max-w-2xl animate-fade-in-up motion-reduce:animate-none">
          <p className="text-xs font-bold uppercase tracking-widest text-muted">Recursos</p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-yhelp-black dark:text-white md:text-4xl">
            Ferramenta completa para operações de suporte
          </h2>
          <p className="mt-4 text-neutral-600 dark:text-neutral-400">
            Desenvolvido pela ySec com a mesma linguagem visual do painel autenticado — sem ruído,
            foco na operação.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature, i) => (
            <article
              key={feature.title}
              className="group surface-card border-l-4 border-l-transparent p-6 transition-all duration-normal ease-out hover:border-l-yhelp-yellow hover:shadow-md motion-reduce:transition-none list-stagger opacity-0 dark:hover:shadow-none"
              style={{ animationDelay: `${160 + i * 70}ms` }}
            >
              <div className="mb-4 inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 text-yhelp-black transition-colors duration-fast group-hover:border-yhelp-yellow group-hover:bg-yhelp-yellow/10 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white dark:group-hover:bg-yhelp-yellow/10">
                {feature.icon}
              </div>
              <h3 className="text-base font-bold text-yhelp-black dark:text-white">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Platform */}
      <section
        id="plataforma"
        className="border-y border-neutral-200 bg-white py-20 dark:border-neutral-800 dark:bg-neutral-900 md:py-28"
      >
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 lg:grid-cols-2">
          <div className="animate-fade-in-up motion-reduce:animate-none lg:order-2">
            <ProductPreview />
          </div>
          <div className="animate-fade-in-up motion-reduce:animate-none lg:order-1">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Plataforma</p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-yhelp-black dark:text-white md:text-4xl">
              A mesma interface que sua equipe usa no dia a dia
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-600 dark:text-neutral-400">
              Sidebar escura, cards neutros e acentos amarelos só onde importa: CTAs, destaques e
              estado ativo. Chamados, filas, dashboard e base de conhecimento em um só lugar.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-neutral-700 dark:text-neutral-300">
              {[
                'Lista de chamados com filtros e classificação',
                'Detalhe com timeline, SLA e chat flutuante',
                'Tours guiados e dark mode nativo',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-yhelp-yellow text-yhelp-black">
                    <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true">
                      <path
                        d="M2 6l3 3 5-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-yhelp-black py-20 text-white md:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center animate-fade-in-up motion-reduce:animate-none">
          <Logo variant="official" size="md" alt="" className="mx-auto" />
          <h2 className="mt-6 text-3xl font-extrabold tracking-tight md:text-4xl">
            Acesso exclusivo para clientes ySec
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-neutral-400">
            Entre com o e-mail corporativo da sua empresa. Novos usuários são convidados pelo
            administrador do tenant — sem cadastro público.
          </p>
          <div className="mt-8 flex justify-center">
            <LoginButton size="lg" loginLabel="Entrar" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-800 bg-yhelp-black py-10 text-neutral-500">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-6 sm:flex-row">
          <div className="flex items-center gap-3">
            <Logo variant="white" size="sm" alt="yHelp" />
            <span className="text-sm">
              © {new Date().getFullYear()} yHelp · <span className="text-yhelp-yellow">ySec</span>
            </span>
          </div>
          <nav className="flex flex-wrap justify-center gap-6 text-xs" aria-label="Links legais">
            <a href="#" className="transition-colors hover:text-white">
              Termos
            </a>
            <a href="#" className="transition-colors hover:text-white">
              Privacidade
            </a>
            <a href="#" className="transition-colors hover:text-white">
              Suporte
            </a>
          </nav>
        </div>
        <p className="mx-auto mt-6 max-w-6xl px-6 text-center text-xs text-neutral-600 sm:text-left">
          Cloudflare Workers · D1 · WCAG 2.1 AA
        </p>
      </footer>
    </div>
  );
}
